import {
  LOCATION_EXPORT_HEADER_STYLE,
  LOCATION_EXPORT_STATUS_HEADER,
  PAGE_DETAILS,
} from './locationConstants';
import { formatEnableDisableStatus } from '@/modules/financialclose/utils/constants';

/**
 * Setup Location helpers. Filter persistence takes a store argument
 * so this module does not import Vue or Vuex.
 */

/** Vuex key for lb-datatableaction persisted column filters on this page. */
export function tableFilterStorageKey(pageDetails = PAGE_DETAILS) {
  const pageName = pageDetails?.pagename || 'unknown';
  const scopeIdentifier = pageDetails?.cycle_id || 'global';
  return `dt_filter:${pageName}:${scopeIdentifier}`;
}

/**
 * Clear persisted table filters when the datatable is not mounted
 * (filter-empty state replaces the table).
 */
export function clearPersistedTableFilters(store, pageDetails = PAGE_DETAILS) {
  if (!store?.commit || !pageDetails?.pagename) return;
  store.commit('clearTableFilter', tableFilterStorageKey(pageDetails));
}

/**
 * Index threshold docs by location id.
 * Threshold docs: { _id, location (id), threshold, status, locationName? }
 *
 * @param {Array} thresholds
 * @returns {Record<string, object>}
 */
export function joinThresholdsByLocationId(thresholds = []) {
  const map = {};
  (thresholds || []).forEach((doc) => {
    const locationId = doc?.location?._id || doc?.location;
    if (locationId) {
      map[locationId] = doc;
    }
  });
  return map;
}

/**
 * @param {string|number|null|undefined} value
 * @returns {string}
 */
export function formatIngestionType(value) {
  return value || 'Manual';
}

/**
 * @param {boolean|undefined} status
 * @returns {string}
 */
export function formatThresholdStatusLabel(status) {
  return formatEnableDisableStatus(status);
}

/**
 * Merge location list rows with threshold docs for table display.
 *
 * @param {Array} locations
 * @param {Array} [thresholds]
 * @returns {Array}
 */
export function formatLocationRows(locations = [], thresholds = []) {
  const thresholdMap = joinThresholdsByLocationId(thresholds);

  return (locations || []).map((item, index) => {
    const thresholdDoc = thresholdMap[item._id] || null;
    // Prefer fields composed on location GET; fall back to separate threshold docs.
    const setThreshold =
      item.set_threshold ??
      item.setThreshold ??
      item.threshold_status ??
      !!thresholdDoc?.status;
    const thresholdValue =
      item.threshold ?? thresholdDoc?.threshold ?? null;
    const thresholdId = item.threshold_id || thresholdDoc?._id || null;
    const enabled = !!setThreshold;

    return {
      ...item,
      __key: index,
      __formatedkey: index,
      indexno: index + 1,
      scource: item.scource || item.source || '-',
      ingestion_type: formatIngestionType(item.ingestion_type),
      status: item.status !== false,
      threshold: enabled && thresholdValue != null ? thresholdValue : '-',
      thresholdStatus: formatThresholdStatusLabel(enabled),
      setThreshold: enabled,
      thresholdId,
      thresholdValue,
    };
  });
}

/**
 * Validate Setup Location form before save.
 *
 * @param {object} data
 * @returns {Record<string, string>}
 */
export function validateLocationForm(data = {}) {
  const errors = {};

  if (!data.name || typeof data.name !== 'string' || !data.name.trim() || data.name.trim().length < 2) {
    errors.name = 'Name is required.';
  }

  if (
    !data.description ||
    typeof data.description !== 'string' ||
    !data.description.trim() ||
    data.description.trim().length < 2
  ) {
    errors.description = 'Description is required.';
  }

  if (
    !data.country ||
    (Array.isArray(data.country) && data.country.length === 0) ||
    (typeof data.country === 'string' && !data.country.trim())
  ) {
    errors.country = 'Country is required.';
  }

  if (!data.type || typeof data.type !== 'string' || !data.type.trim()) {
    errors.type = 'Type is required.';
  }

  if (data.type === 'Other') {
    if (!data.newtype || typeof data.newtype !== 'string' || !data.newtype.trim()) {
      errors.newtype = 'New type is required.';
    }
  }

  if (data.setThreshold) {
    const threshold = data.threshold;
    const parsed = Number(threshold);
    if (threshold === '' || threshold == null || Number.isNaN(parsed) || parsed < 0) {
      errors.threshold = 'Threshold is required.';
    }
  }

  return errors;
}

/**
 * Build editable form state from a formatted table row.
 *
 * @param {object} row
 * @returns {object}
 */
export function buildLocationFormFromRow(row = {}) {
  return {
    _id: row._id || null,
    name: row.name || '',
    description: row.description || '',
    country: row.country || '',
    type: row.type || '',
    newtype: '',
    setThreshold: !!row.setThreshold,
    threshold: row.thresholdValue ?? row.threshold ?? null,
    thresholdId: row.thresholdId || null,
  };
}

/**
 * Resolve a table cell value for Excel export.
 *
 * @param {object} row
 * @param {object} header
 * @returns {*}
 */
export function getLocationExportCellValue(row = {}, header = {}) {
  if (header.value === 'status') {
    return formatEnableDisableStatus(row?.status !== false);
  }

  const value = row?.[header.value];

  if (header.value === 'threshold') {
    if (value === '-' || value === '' || value == null) return '';
    const parsed = Number(value);
    if (!Number.isNaN(parsed)) {
      return { v: parsed, t: 'n', z: '#,##' };
    }
  }

  if (value === undefined || value === null) return '';
  return value;
}

/**
 * Build a styled Excel header cell for Location export.
 *
 * @param {string} text
 * @returns {object}
 */
export function buildLocationExportHeaderCell(text = '') {
  return {
    v: text,
    s: { ...LOCATION_EXPORT_HEADER_STYLE },
  };
}

/**
 * Build sheet rows for Location export (title + header + data).
 *
 * @param {object} options
 * @param {Array} [options.rows]
 * @param {Array} [options.headers]
 * @param {string} [options.title]
 * @returns {{ dt: Array, visibleHeaders: Array }}
 */
export function buildLocationExportSheetData({
  rows = [],
  headers = [],
  title = 'Location',
} = {}) {
  const visibleHeaders = (headers || []).filter(
    (header) =>
      header.visible !== false &&
      header.value !== 'action' &&
      header.datatype !== 'action'
  );
  const exportHeaders = [...visibleHeaders, LOCATION_EXPORT_STATUS_HEADER];

  const dt = [
    [undefined],
    [undefined, { v: title, s: { font: { bold: true, sz: 20 } } }],
    [undefined],
    [
      undefined,
      ...exportHeaders.map((header) => buildLocationExportHeaderCell(header.text)),
    ],
  ];

  (rows || []).forEach((row) => {
    const dataRow = [undefined];
    exportHeaders.forEach((header) => {
      dataRow.push(getLocationExportCellValue(row, header));
    });
    dt.push(dataRow);
  });

  return { dt, visibleHeaders: exportHeaders };
}

/**
 * Download Setup Location table data as an Excel file.
 *
 * @param {object} options
 * @param {Array} [options.rows]
 * @param {Array} [options.headers]
 * @param {string} [options.filename]
 */
export function downloadLocationExcel({
  rows = [],
  headers = [],
  filename = 'Location',
} = {}) {
  const styledxlsx = require('sheetjs-style');
  const safeFilename = String(filename || 'Location')
    .trim()
    .replace(/[<>:"/\\|?*]+/g, '')
    .slice(0, 120) || 'Location';
  const { dt, visibleHeaders } = buildLocationExportSheetData({
    rows,
    headers,
    title: safeFilename,
  });

  const worksheet = styledxlsx.utils.aoa_to_sheet(dt);
  const colWidths = visibleHeaders.map((header) => {
    const base = (header.text || '').length;
    return { wch: Math.min(Math.max(base + 4, 12), 40) };
  });
  worksheet['!cols'] = [{ wch: 3 }, ...colWidths];

  const workbook = styledxlsx.utils.book_new();
  styledxlsx.utils.book_append_sheet(
    workbook,
    worksheet,
    safeFilename.slice(0, 31)
  );
  styledxlsx.writeFile(workbook, `${safeFilename}.xlsx`);
}
